import { useEffect, useId, useState } from "react";
import { Check, FileInput, Loader2, ShieldCheck } from "lucide-react";
import type { Locale } from "@/shared/i18n/translations";

const COPY = {
  en: {
    title: "Paste a transcript",
    body: "Use text you are authorized to process. Remove personal details that are not needed for your review.",
    label: "Conversation transcript",
    placeholder: "Paste the conversation transcript here…",
    authorization:
      "I confirm that I am authorized to process this material and have provided any required notices or obtained any required permissions.",
    save: "Save transcript",
    update: "Update transcript",
    saving: "Saving…",
    saved: "Transcript saved",
    required: "Add transcript text and confirm your authorization.",
    failed: "The transcript could not be saved. Please try again.",
  },
  uk: {
    title: "Вставте транскрипт",
    body: "Використовуйте текст, який ви маєте право обробляти. Видаліть персональні дані, не потрібні для аналізу.",
    label: "Транскрипт розмови",
    placeholder: "Вставте сюди транскрипт розмови…",
    authorization:
      "Підтверджую, що маю право обробляти цей матеріал і надав(ла) всі необхідні повідомлення або отримав(ла) потрібні дозволи.",
    save: "Зберегти транскрипт",
    update: "Оновити транскрипт",
    saving: "Збереження…",
    saved: "Транскрипт збережено",
    required: "Додайте текст транскрипту та підтвердьте право на обробку.",
    failed: "Не вдалося зберегти транскрипт. Спробуйте ще раз.",
  },
  pl: {
    title: "Wklej transkrypcję",
    body: "Użyj tekstu, do którego przetwarzania masz uprawnienie. Usuń dane osobowe, które nie są potrzebne do analizy.",
    label: "Transkrypcja rozmowy",
    placeholder: "Wklej tutaj transkrypcję rozmowy…",
    authorization:
      "Potwierdzam, że mam uprawnienie do przetwarzania tego materiału oraz przekazałem wymagane informacje lub uzyskałem wymagane zgody.",
    save: "Zapisz transkrypcję",
    update: "Aktualizuj transkrypcję",
    saving: "Zapisywanie…",
    saved: "Transkrypcja zapisana",
    required: "Dodaj tekst transkrypcji i potwierdź uprawnienie.",
    failed: "Nie udało się zapisać transkrypcji. Spróbuj ponownie.",
  },
  ro: {
    title: "Lipește o transcriere",
    body: "Folosește text pe care ești autorizat să îl prelucrezi. Elimină datele personale care nu sunt necesare analizei.",
    label: "Transcrierea conversației",
    placeholder: "Lipește aici transcrierea conversației…",
    authorization:
      "Confirm că sunt autorizat să prelucrez acest material și că am furnizat informările necesare sau am obținut permisiunile necesare.",
    save: "Salvează transcrierea",
    update: "Actualizează transcrierea",
    saving: "Se salvează…",
    saved: "Transcriere salvată",
    required: "Adaugă textul și confirmă autorizarea.",
    failed: "Transcrierea nu a putut fi salvată. Încearcă din nou.",
  },
} as const;

export function TranscriptImportPanel({
  locale,
  transcript,
  authorized,
  autoFocus = false,
  onSave,
}: {
  locale: Locale;
  transcript: string | null;
  authorized: boolean;
  autoFocus?: boolean;
  onSave: (transcript: string) => Promise<void>;
}) {
  const copy = COPY[locale] ?? COPY.en;
  const inputId = useId();
  const [value, setValue] = useState(transcript ?? "");
  const [confirmed, setConfirmed] = useState(authorized);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(transcript ?? "");
    setConfirmed(authorized);
    setSaved(false);
  }, [transcript, authorized]);

  const submit = async () => {
    const next = value.trim();
    if (!next || !confirmed || saving) {
      setError(copy.required);
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await onSave(next);
      setSaved(true);
    } catch {
      setError(copy.failed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="eq-source-card eq-transcript-import">
      <header>
        <span className="eq-recording-icon">
          <FileInput size={18} aria-hidden="true" />
        </span>
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.body}</p>
        </div>
      </header>
      <label htmlFor={inputId}>{copy.label}</label>
      <textarea
        id={inputId}
        autoFocus={autoFocus}
        rows={9}
        value={value}
        placeholder={copy.placeholder}
        onChange={(event) => {
          setValue(event.target.value);
          setSaved(false);
          setError(null);
        }}
      />
      {!authorized && (
        <label className="eq-source-authorization">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <ShieldCheck size={18} aria-hidden="true" />
          <span>{copy.authorization}</span>
        </label>
      )}
      {error && <p className="eq-source-error" role="alert">{error}</p>}
      <footer>
        {saved && (
          <span className="eq-source-saved" role="status">
            <Check size={14} aria-hidden="true" />
            {copy.saved}
          </span>
        )}
        <button
          type="button"
          className="eq-button eq-button--primary"
          disabled={saving || !value.trim() || !confirmed}
          onClick={() => void submit()}
        >
          {saving && <Loader2 size={15} className="eq-spin" aria-hidden="true" />}
          {saving ? copy.saving : transcript ? copy.update : copy.save}
        </button>
      </footer>
    </section>
  );
}
